import React, {useState, useEffect, useContext} from "react";
import {
  createAuth0Client,
  Auth0Client,
  Auth0ClientOptions,
  RedirectLoginOptions,
  PopupLoginOptions,
  GetTokenSilentlyOptions,
  LogoutOptions,
  User,
  GetTokenWithPopupOptions,
  PopupConfigOptions,
} from "@auth0/auth0-spa-js";;

interface AuthProviderProps {
  children: React.ReactNode;
}

export const DEFAULT_REDIRECT_CALLBACK = (): void => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

interface Auth0ContextInterface {
  isAuthenticated: boolean;
  user: User | undefined;
  isLoading: boolean;
  popupOpen: boolean;
  loginWithPopup: (params?: PopupLoginOptions) => Promise<void>;
  handleRedirectCallback: () => Promise<void>;
  loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>;
  getAccessTokenWithPopup: (options?: GetTokenWithPopupOptions, config?: PopupConfigOptions) => Promise<string | undefined>;
  getAccessTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<string>;
  logout: (options?: LogoutOptions) => void;
}

interface Auth0ProviderProps extends Auth0ClientOptions {
  children: React.ReactNode;
  onRedirectCallback?: (appState: unknown) => void;
}

const defaultContext: Partial<Auth0ContextInterface> = {};

const Auth0Context = React.createContext<Auth0ContextInterface>(defaultContext as Auth0ContextInterface);
export const useAuth0 = () => useContext(Auth0Context);

const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}: Auth0ProviderProps): JSX.Element => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | undefined>();
  const [auth0Client, setAuth0] = useState<Auth0Client | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    const initAuth0 = async () => {
      let auth0FromHook: Auth0Client = await createAuth0Client(initOptions);
      
      setAuth0(auth0FromHook);

      if (
        window.location.search.includes("code=") &&
        window.location.search.includes("state=")
      ) {
        const { appState } = await auth0FromHook.handleRedirectCallback();
        onRedirectCallback(appState);
      }

      const isAuthenticated = await auth0FromHook.isAuthenticated();

      
      if (isAuthenticated) {
        const user = await auth0FromHook.getUser();
        setUser(user);
      }
      setIsAuthenticated(isAuthenticated);

      setIsLoading(false);
    };
    initAuth0();
    // eslint-disable-next-line
  }, []);

  const loginWithPopup = async (params: PopupLoginOptions = {}): Promise<void> => {
    if (!auth0Client) return;
    setPopupOpen(true);
    try {
      await auth0Client.loginWithPopup(params);
    } catch (error) {
      console.error(error);
    } finally {
      setPopupOpen(false);
    }
    const user = await auth0Client.getUser();
    setUser(user);
    setIsAuthenticated(true);
  };

  const handleRedirectCallback = async (): Promise<void> => {
    if (!auth0Client) return;
    setIsLoading(true);
    await auth0Client.handleRedirectCallback();
    const user = await auth0Client.getUser();
    setUser(user);
    setIsAuthenticated(true);
    setIsLoading(false);
  };
  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        isLoading,
        popupOpen,
        loginWithPopup,
        handleRedirectCallback,
        loginWithRedirect: (options) => auth0Client!.loginWithRedirect(options),
        getAccessTokenWithPopup: (options, config) => auth0Client!.getTokenWithPopup(options, config),
        getAccessTokenSilently: (options) => auth0Client!.getTokenSilently(options),
        logout: (options) => auth0Client!.logout(options),
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};


const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN || ""}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID || ""}
      cacheLocation={"localstorage"}
      authorizationParams={{
        audience: process.env?.REACT_APP_AUTH0_AUDIENCE || "",
        redirect_uri: window.location.origin,
      }}
    >
      {children}
    </Auth0Provider>
  );
};

export default AuthProvider;
